import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import type { ValidationError } from "~/common/types";
import {
  CreateTenantDocument,
  type CreateTenantMutation,
  type CreateTenantMutationVariables,
  GetTenantsDocument,
} from "~/features/admin/tenants/gqls";
import { TENANT_SHORTCODE_PREFIX, type TenantFormData } from "~/features/admin/tenants/types";
import { translateError } from "~/lib/errors";

interface UseTenantCreateOptions {
  onSuccess?: (data: TenantFormData) => void;
  onError?: (error: Pick<ValidationError, "fields">) => void;
}

/** Create a tenant. The shortCode is sent with the `u-` prefix prepended. */
export function useTenantCreate({ onSuccess, onError }: UseTenantCreateOptions = {}) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [createTenant] = useMutation<CreateTenantMutation, CreateTenantMutationVariables>(CreateTenantDocument, {
    refetchQueries: [GetTenantsDocument],
  });

  const submit = useCallback(
    async (formData: TenantFormData) => {
      setProcessing(true);
      try {
        const res = await createTenant({
          variables: {
            input: {
              name: formData.name,
              shortCode: `${TENANT_SHORTCODE_PREFIX}${formData.shortCode}`,
              allowAutoApproveChangeRequests: formData.allowAutoApproveChangeRequests,
            },
          },
        });
        match(res.data?.cnutCreateTenant)
          .with({ __typename: "ValidationError" }, (e) => onError?.(e))
          .with({ __typename: "TenantCreateOutput" }, () => {
            toast.success(t("feature.common.createdSuccessfully", { model: "tenant" }));
            onSuccess?.(formData);
          })
          .otherwise(() => {});
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [createTenant, t, onSuccess, onError],
  );

  return { submit, processing };
}
